import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment'; // NOSONAR: This import is necessary for moment usage. Default import doesn't work.
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfiguration } from '../../core/models/app-configuration.model';
import {
  AuthorizationEndRequest,
  AuthorizationEndResponse,
  AuthorizationStartRequest,
  AuthorizationStartResponse,
  AuthorizationStatusResponse,
} from '../model/authorization.model';

/**
 * Authorization Service
 * @description Angular Service class with method used for request permission for update contact or
 * update 2fa of an user
 */
@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  constructor(private readonly http: HttpClient, private readonly appConfig: AppConfiguration) { }

  getResourceUrl(): string {
    return this.appConfig.serverApiUrl + '/api/v1/authorize';
  }

  /**
   * Request for start authorization flow for a certain operation type
   * @param req  The authorization start request information with operation Type and channel and channel info
   */
  startAuthorization(req: AuthorizationStartRequest, authRequestId?: string): Observable<AuthorizationStartResponse> {
    let options = {};
    if (authRequestId) {
      options = { headers: new HttpHeaders({ authPermission: authRequestId }) };
    }
    return this.http.post<AuthorizationStartResponse>(this.getResourceUrl(), req, options);
  }

  /**
   * Request for start authorization flow for a certain operation type
   * @param req  The authorization start request information with operation Type and channel and channel info
   */
  startAuthorization2Fa(req: AuthorizationStartRequest): Observable<AuthorizationStartResponse> {
    return this.http.post<AuthorizationStartResponse>(this.getResourceUrl() + '/2fa', req);
  }

  /**
   * Get the status of a requested authorization
   * @param authRequestId Identifier of the authorization request
   */
  getAuthorizationStatus(authRequestId: string): Observable<AuthorizationStatusResponse> {
    return this.http
      .get<AuthorizationStatusResponse>(`${this.getResourceUrl()}/${authRequestId}`)
      .pipe(map((res: AuthorizationStatusResponse) => this.convertDateFromServer(res)));
  }

  /**
   * Complete the authorization flow and obtain the final authorization
   * @param authRequestId Identifier of the authorization request
   * @param req The authorization end request information
   */
  endAuthorization(authRequestId: string, req: AuthorizationEndRequest): Observable<AuthorizationEndResponse> {
    return this.http.put<AuthorizationEndResponse>(`${this.getResourceUrl()}/${authRequestId}`, req);
  }

  /**
   * Utility function for convert server date to client date for every field of Authorization Status response
   * @param res Authorization status object contain date variables
   */
  protected convertDateFromServer(res: AuthorizationStatusResponse): AuthorizationStatusResponse {
    if (res) {
      res.nextRequestNotBefore = res.nextRequestNotBefore ? moment(res.nextRequestNotBefore) : undefined;
    }
    return res;
  }
}
